import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { gapi, fmtDateTime } from "../api";
import { useGAuth } from "../GAuth";
import { RUOLI_CONTATTO, STATI_CONTATTO, PRIORITA } from "../labels";
import { PageHeader, Panel, Btn, Badge, inputCls } from "../components/ui";
import { SavedFilters, Pager, downloadCsv } from "../components/common";

const EMPTY = { q: "", ruolo: "", stato: "", responsabileId: "", tag: "", priorita: "", archiviati: "0", sort: "recenti" };

export default function Contatti() {
  const { can } = useGAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [showF, setShowF] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => { gapi.get("/users/options").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => gapi.get("/contacts", { params: { ...f, page } }).then((r) => setData(r.data)), 200);
    return () => clearTimeout(t);
  }, [f, page]);

  const set = (k) => (e) => { setPage(1); setF({ ...f, [k]: e.target.value }); };
  const uname = (id) => users.find((u) => u.id === id)?.nome || "—";

  return (
    <div data-testid="g-contacts-page">
      <PageHeader eyebrow="Banca dati" title="Clienti e contatti" desc="Archivio unico: una persona può avere più ruoli senza schede duplicate."
        actions={<>
          {can("contatti", "esportare") && <Btn variant="ghost" onClick={() => downloadCsv("/contacts/export", f, "contatti-casamore.csv")} data-testid="contacts-export"><Download className="h-4 w-4" /> Esporta CSV</Btn>}
          {can("contatti", "creare") && <Btn onClick={() => navigate("/gestionale/contatti/nuovo")} data-testid="contacts-new"><Plus className="h-4 w-4" /> Nuovo contatto</Btn>}
        </>} />
      <Panel className="mb-5">
        <input className={`${inputCls} mb-3`} placeholder="Cerca nome, telefono, email, CF…" value={f.q} onChange={set("q")} data-testid="contacts-search" />
        <button className="sm:hidden mb-3 text-sm font-semibold text-brand" onClick={() => setShowF(!showF)} data-testid="contacts-toggle-filters">{showF ? "Nascondi filtri" : "Mostra altri filtri"}</button>
        <div className={`${showF ? "grid" : "hidden"} sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3`}>
          <select className={inputCls} value={f.ruolo} onChange={set("ruolo")} data-testid="contacts-filter-ruolo"><option value="">Tutti i ruoli</option>{Object.entries(RUOLI_CONTATTO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={inputCls} value={f.stato} onChange={set("stato")} data-testid="contacts-filter-stato"><option value="">Tutti gli stati</option>{Object.entries(STATI_CONTATTO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={inputCls} value={f.responsabileId} onChange={set("responsabileId")} data-testid="contacts-filter-resp"><option value="">Tutti i responsabili</option>{users.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}</select>
          <select className={inputCls} value={f.priorita} onChange={set("priorita")}><option value="">Tutte le priorità</option>{Object.entries(PRIORITA).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select className={inputCls} value={f.tag} onChange={set("tag")}><option value="">Tutti i tag</option>{(data?.tags || []).map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <select className={inputCls} value={f.sort} onChange={set("sort")}><option value="recenti">Più recenti</option><option value="nome">Nome A-Z</option><option value="richiamo">Prossimo richiamo</option><option value="ultimo">Ultimo contatto</option></select>
          <select className={inputCls} value={f.archiviati} onChange={set("archiviati")} data-testid="contacts-filter-archived"><option value="0">Attivi</option><option value="1">Archiviati</option></select>
        </div>
        <div className="mt-3 flex justify-between gap-3 flex-wrap">
          <SavedFilters pagina="contatti" current={f} onApply={(x) => { setPage(1); setF({ ...EMPTY, ...x }); }} />
          <button className="text-xs text-anthracite/50 hover:underline" onClick={() => setF(EMPTY)}>Azzera filtri</button>
        </div>
      </Panel>
      <Panel>
        {!data ? <p className="text-sm">Caricamento…</p> : (
          <>
            <ul className="divide-y divide-border-subtle">
              {data.items.map((c) => (
                <li key={c.id}>
                  <Link to={`/gestionale/contatti/${c.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 hover:bg-accent-subtle/60 -mx-2 px-2 rounded-lg" data-testid="contact-row">
                    <span className="flex-1 min-w-[200px]"><b>{c.displayName}</b><br /><span className="text-xs text-anthracite/50">{(c.telefoni || [])[0]?.numero || ""} {(c.emails || [])[0]?.indirizzo || ""}</span></span>
                    <span className="flex flex-wrap gap-1">{(c.ruoli || []).map((r) => <Badge key={r}>{RUOLI_CONTATTO[r] || r}</Badge>)}</span>
                    <Badge tone={c.stato === "nuovo" ? "blue" : "neutral"}>{STATI_CONTATTO[c.stato] || c.stato}</Badge>
                    <span className="text-xs text-anthracite/50 w-40">Resp.: {uname(c.responsabileId)}<br />Ultimo: {fmtDateTime(c.ultimoContatto)}</span>
                  </Link>
                </li>
              ))}
              {!data.items.length && <li className="py-6 text-sm text-anthracite/45">Nessun contatto trovato.</li>}
            </ul>
            <Pager data={data} page={page} setPage={setPage} />
          </>
        )}
      </Panel>
    </div>
  );
}
